import React, { useEffect, useMemo, useRef, useState } from "react";
import SnapshotImg from "../../components/SnapshotImg";
import CamerasStyles from "../../components/cameras/CamerasStyles";
import { ActivityLine, AlertLine, DOOR_ALARM_FRAME, LiveBadge, WatchTag, cameraProblem, healthFor } from "../../components/cameras/CameraTile";
import { DoorAlarmBanners, DoorChips, DoorsLine } from "../../components/cameras/DoorAlarm";
import { doorChipsAlarm, doorChipsForCamera } from "../../features/security/doors";
import { phoneUi } from "../ui/phoneUi";
import { recentAlerts, recentSince, tileAlert, tileWatch } from "../../features/security/nightWatch";
import { useNightWatchAlerts, useNightWatchStatus } from "../../features/security/useNightWatch";
import NightWatchBar from "./cameras/NightWatchBar";
import PhoneLocks from "./cameras/PhoneLocks";
import PhoneLights from "./cameras/PhoneLights";
import RecentAlerts from "./cameras/RecentAlerts";
import { STATUS_TONE, summarize } from "../../components/cameras/StatusPill";
import {
  useClipList,
  useClipThumbs,
  useClipVideo,
  useRelay,
  useRetention,
  useStationActivity,
} from "../../components/cameras/useCameraData";
import {
  CAMERA_NAMES,
  clipAt,
  clipStartMinute,
  fmtClock,
  getCfHeaders,
  nextClip,
  shiftDate,
  snapshotUrl,
  sortClips,
  streamUrl,
  todayCentral,
} from "../../utils/cameraRelay";
import "../mobile.css";

// The phone Cameras page (plan Phase 1): the old phone page (MobileCamerasPage, deleted 2026-10-13) moved
// into the phone shell, same look. Live = one column of snapshot tiles with what JobQ says each
// station is doing, tap for the live stream; Playback = a camera's day as a list of 2-minute clips.
// Layout only: the relay, health, JobQ activity, clip list / video / thumbnails come from
// components/cameras/useCameraData.js and utils/cameraRelay.js (the desktop page's own).
//
// Clip filenames are the camera Pi's LOCAL Central time, read as written (never converted).

const clipLabel = (file) => {
  const minute = clipStartMinute(file);
  return minute == null ? file : fmtClock(minute, { seconds: true });
};

const centered = { minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" };

export default function CamerasScreen() {
  const [mode, setMode] = useState("live");

  // The relay's cameras + health (every minute) and JobQ station activity (Live only).
  const { cameras, health, healthError } = useRelay();
  const { activity } = useStationActivity(mode === "live");
  // The night watch (jobq-api /security/status): the line under the status line + the tiles' tags.
  const { status: security, error: securityError, reload: reloadSecurity, pollMs } = useNightWatchStatus(true);
  const allCameras = useMemo(() => cameras || [], [cameras]);
  // Recent alerts (camera + door, the last 24 h): read at the status's own cadence (60 s, 10 s while a door
  // holds an alert or the siren is on).
  const { alerts: alertsRaw, error: alertsError } = useNightWatchAlerts(mode === "live", recentSince(), { limit: 30, ms: pollMs });
  const recent = alertsRaw ? recentAlerts(alertsRaw) : null;

  // Live
  const [selectedCam, setSelectedCam] = useState(null);
  const [stationFilter, setStationFilter] = useState("All");

  // Playback
  const [pbStation, setPbStation] = useState("");
  const [pbIndex, setPbIndex] = useState(0);
  const [pbDate, setPbDate] = useState(todayCentral);
  const [pbSelectedFile, setPbSelectedFile] = useState("");
  const [pbAutoAdvance, setPbAutoAdvance] = useState(true);
  const [pbControlsOpen, setPbControlsOpen] = useState(true);
  const activeClipRef = useRef(null);
  const playNext = useRef(false); // auto-advance picked the clip: start it when it loads
  const pendingClip = useRef(null); // Watch clip from an alert: {key, minute} until that day's list arrives

  const playback = mode === "playback";

  const stationOptions = useMemo(() => [...new Set(allCameras.map((c) => c.station))], [allCameras]);
  const countFor = (station) => allCameras.filter((c) => c.station === station).length;
  const pbIndexOptions = useMemo(
    () => allCameras.filter((c) => c.station === pbStation).map((c) => c.index),
    [allCameras, pbStation],
  );

  // First station once the list arrives; a camera index the station doesn't have goes back to 0.
  useEffect(() => {
    if (!pbStation && stationOptions.length) setPbStation(stationOptions[0]);
  }, [pbStation, stationOptions]);
  useEffect(() => {
    if (pbIndexOptions.length && !pbIndexOptions.includes(pbIndex)) setPbIndex(0);
  }, [pbIndexOptions, pbIndex]);

  const retention = useRetention(playback && pbStation ? { station: pbStation, index: pbIndex } : null);
  const availableDates = useMemo(() => (retention ? retention.dates : []), [retention]);
  const availableDateSet = useMemo(() => new Set(availableDates), [availableDates]);
  const recentDates = useMemo(() => availableDates.slice(-10).reverse(), [availableDates]);

  const clipList = useClipList(pbStation, pbIndex, pbDate, playback);
  const pbFiles = clipList.files;
  // Only a clip of THIS camera's list: right after a camera / day change the old pick is still set.
  const videoFile = pbFiles.includes(pbSelectedFile) ? pbSelectedFile : "";
  const video = useClipVideo(pbStation, pbIndex, videoFile, playback);
  const thumbs = useClipThumbs(pbStation, pbIndex, pbDate, playback);

  // A new list starts on its newest clip (the relay lists newest first), or on the clip an alert's
  // Watch clip asked for once that camera's day has loaded.
  const pbKey = `${pbStation}|${pbIndex}|${pbDate}`;
  useEffect(() => {
    const want = pendingClip.current;
    if (want && want.key === pbKey) {
      if (clipList.loading) return;
      pendingClip.current = null;
      const hit = clipAt(sortClips(pbFiles), want.minute);
      setPbSelectedFile(hit ? hit.file : pbFiles[0] || "");
      return;
    }
    setPbSelectedFile(pbFiles.length ? pbFiles[0] : "");
  }, [pbFiles, pbKey, clipList.loading]);

  // An alert's Watch clip: that camera, that Central day, the clip covering 30 s before it was seen.
  function watchAlertClip(camera, day, minute) {
    pendingClip.current = { key: `${camera.station}|${camera.index}|${day}`, minute };
    playNext.current = false;
    setPbStation(camera.station);
    setPbIndex(camera.index);
    setPbDate(day);
    setPbControlsOpen(false);
    setMode("playback");
  }

  useEffect(() => {
    const el = activeClipRef.current;
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [pbSelectedFile]);

  // Auto-advance plays on to the clip that FOLLOWS in time.
  function handleVideoEnded() {
    if (!pbAutoAdvance || !pbSelectedFile) return;
    const next = nextClip(sortClips(pbFiles), pbSelectedFile);
    if (next) {
      playNext.current = true;
      setPbSelectedFile(next.file);
    }
  }

  function pickClip(f) {
    playNext.current = false;
    setPbSelectedFile(f);
  }

  function handleVideoLoaded(e) {
    if (!playNext.current) return;
    playNext.current = false;
    // A phone may refuse to start a video with sound on its own; then the clip waits for a tap.
    const p = e.currentTarget.play();
    if (p && p.catch) p.catch(() => {});
  }

  const filteredCameras = useMemo(
    () => (stationFilter === "All" ? allCameras : allCameras.filter((c) => c.station === stationFilter)),
    [allCameras, stationFilter],
  );

  if (cameras === null) {
    return (
      <div className="jq-cameras jq-cameras-loading" data-testid="phone-cameras">
        <i className="fas fa-circle-notch fa-spin fa-2x text-white mb-3" aria-hidden="true"></i>
        <span className="text-white">Initializing Security Feed...</span>
      </div>
    );
  }

  const st = summarize(allCameras, health, healthError);
  const camName = (station, i) => CAMERA_NAMES[station]?.[i];

  return (
    <div className="jq-cameras" data-testid="phone-cameras">
      <CamerasStyles />

      {/* Live / Playback, under the phone's top bar */}
      <div className="jq-cameras-bar">
        <div className="jqc-seg jq-cameras-seg" role="group" aria-label="Mode">
          <button type="button" aria-pressed={mode === "live"} onClick={() => setMode("live")}>Live</button>
          <button type="button" aria-pressed={playback} onClick={() => setMode("playback")}>Playback</button>
        </div>
      </div>

      {/* One status line for the whole camera system */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 12, color: "#a1a9b3", borderBottom: "1px solid #22272d" }}>
        <span className="jqc-dot" style={{ background: STATUS_TONE[st.tone] }} />
        <span style={{ color: st.tone === "ok" || st.tone === "muted" ? "#c3cad2" : STATUS_TONE[st.tone] }}>{st.main}</span>
        {health?.disk && <span>· disk {health.disk.used_pct}%</span>}
      </div>
      <NightWatchBar status={security} error={securityError} cameras={allCameras} onChanged={reloadSecurity} />
      {/* The door alarm (2026-10-25): the doors line, then the hold / siren banners. No link to /doors: it is not a phone page. */}
      {security?.doors && (
        <div style={{ padding: "6px 12px", borderBottom: "1px solid #22272d" }}><DoorsLine doors={security.doors} /></div>
      )}
      <DoorAlarmBanners doors={security?.doors} ui={phoneUi} onChanged={reloadSecurity} padding="8px 12px" />
      <PhoneLocks />
      <PhoneLights />
      {mode === "live" && (
        <RecentAlerts alerts={recent} error={alertsError} cameras={allCameras} onWatchClip={watchAlertClip} />
      )}

      {/* ============ LIVE ============ */}
      {mode === "live" && (
        <div className="p-2">
          <div className="d-flex overflow-auto gap-2 mb-3 pb-1" style={{ whiteSpace: "nowrap" }}>
            <button type="button" className="jqc-chip" aria-pressed={stationFilter === "All"} onClick={() => setStationFilter("All")}>
              All
            </button>
            {stationOptions.map((station) => (
              <button key={station} type="button" className="jqc-chip" aria-pressed={stationFilter === station} onClick={() => setStationFilter(station)}>
                {station}
                {countFor(station) > 1 && <span style={{ fontSize: 11, opacity: 0.65 }}>{countFor(station)}</span>}
              </button>
            ))}
          </div>

          {filteredCameras.length === 0 && <div className="text-center text-muted py-5">No cameras found.</div>}

          {/* One column: the frame at its real shape, the name and what JobQ says underneath. */}
          <div className="d-flex flex-column gap-3" data-testid="phone-camera-tiles">
            {filteredCameras.map((cam) => {
              const problem = cameraProblem(healthFor(health, cam));
              const doorChips = doorChipsForCamera(security?.doors, cam.key);
              const doorAlarm = doorChipsAlarm(doorChips);
              return (
                <button key={cam.key} type="button" className="jqc-tile" onClick={() => setSelectedCam(cam)} aria-label={`Watch ${cam.label} live`}
                        data-door-alarm={doorAlarm ? "true" : undefined} style={doorAlarm ? DOOR_ALARM_FRAME : undefined}>
                  <span className="jqc-frame" style={{ aspectRatio: String(cam.aspect) }}>
                    <SnapshotImg
                      src={snapshotUrl(cam.station, cam.index)}
                      alt=""
                      authHeaders={getCfHeaders()}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {problem && (
                      <span style={{ position: "absolute", top: 8, left: 8, padding: "3px 9px", borderRadius: 999, background: "rgba(20,14,4,.85)", color: "#f0a030", fontSize: 12, fontWeight: 600 }}>
                        {problem}
                      </span>
                    )}
                    <WatchTag watch={tileWatch(security, cam.key)} />
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 4, padding: "9px 12px 11px", minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{cam.station}</span>
                      {cam.sub && <span style={{ color: "#a1a9b3", fontSize: 13 }}>{cam.sub}</span>}
                      <span style={{ flex: 1 }} />
                      <LiveBadge problem={problem} />
                    </span>
                    {tileAlert(security, cam.key)
                      ? <AlertLine lastAlert={tileAlert(security, cam.key)} />
                      : <ActivityLine camera={cam} activity={cam.jobqStation ? activity[cam.jobqStation] : null} />}
                    <DoorChips chips={doorChips} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ FULL SCREEN LIVE STREAM ============ */}
      {selectedCam && (
        <div className="jq-cameras-overlay" role="dialog" aria-label={`${selectedCam.label} live`} data-testid="phone-camera-live">
          <div className="d-flex justify-content-between align-items-center p-2" style={{ backgroundColor: "rgba(0,0,0,0.8)" }}>
            <span className="text-white fw-bold">{selectedCam.label}</span>
            <button type="button" className="btn btn-sm btn-outline-light" aria-label="Close" onClick={() => setSelectedCam(null)}>
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0 }}>
            <img src={streamUrl(selectedCam.station, selectedCam.index)} alt="Full View" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
        </div>
      )}

      {/* ============ PLAYBACK ============ */}
      {playback && (
        <div className="p-2">
          {/* Controls (collapsible) */}
          <div className="rounded mb-3" style={{ backgroundColor: "#2d2d2d", border: "1px solid #444" }}>
            <button
              type="button"
              className="d-flex justify-content-between align-items-center p-2 w-100 border-0 text-start"
              style={{ background: "transparent" }}
              aria-expanded={pbControlsOpen}
              onClick={() => setPbControlsOpen((prev) => !prev)}
            >
              <span className="text-white fw-bold">
                <i className="fas fa-sliders-h me-2" aria-hidden="true"></i>
                {pbStation || "Station"} / {camName(pbStation, pbIndex) || `Cam ${pbIndex + 1}`} / {pbDate}
              </span>
              <i className={`fas fa-chevron-${pbControlsOpen ? "up" : "down"} text-muted`} aria-hidden="true"></i>
            </button>

            {pbControlsOpen && (
              <div className="p-2 pt-0" style={{ borderTop: "1px solid #444" }}>
                <div className="row g-2 mt-1">
                  <div className="col-6">
                    <label className="text-muted small" htmlFor="phone-pb-station">Station</label>
                    <select
                      id="phone-pb-station"
                      className="form-select form-select-sm bg-dark text-white border-secondary"
                      value={pbStation}
                      onChange={(e) => setPbStation(e.target.value)}
                    >
                      {stationOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="text-muted small" htmlFor="phone-pb-camera">Camera</label>
                    <select
                      id="phone-pb-camera"
                      className="form-select form-select-sm bg-dark text-white border-secondary"
                      value={pbIndex}
                      onChange={(e) => setPbIndex(parseInt(e.target.value, 10))}
                      disabled={pbIndexOptions.length === 0}
                    >
                      {pbIndexOptions.map((i) => <option key={i} value={i}>{camName(pbStation, i) || `Camera ${i + 1}`}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-2">
                  <label className="text-muted small" htmlFor="phone-pb-date">Date</label>
                  <input
                    id="phone-pb-date"
                    type="date"
                    className="form-control form-control-sm bg-dark text-white border-secondary"
                    value={pbDate}
                    onChange={(e) => e.target.value && setPbDate(e.target.value)}
                  />
                  <div className="d-flex gap-2 mt-2">
                    <button type="button" className="btn btn-sm btn-outline-light flex-fill" onClick={() => setPbDate(todayCentral())}>Today</button>
                    <button type="button" className="btn btn-sm btn-outline-light flex-fill" onClick={() => setPbDate(shiftDate(todayCentral(), -1))}>Yesterday</button>
                  </div>

                  {availableDates.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1" style={{ fontSize: "0.7rem" }}>
                        <i className="fas fa-calendar-check me-1" aria-hidden="true"></i>
                        {availableDates.length} days with recordings
                        {availableDateSet.has(pbDate)
                          ? <span className="text-success ms-1"><i className="fas fa-check-circle" aria-hidden="true"></i></span>
                          : <span className="text-warning ms-1">(none for selected)</span>}
                      </small>
                      <div className="d-flex flex-wrap gap-1" style={{ maxHeight: "60px", overflowY: "auto" }}>
                        {recentDates.map((d) => (
                          <button
                            key={d}
                            type="button"
                            className={`btn btn-sm ${d === pbDate ? "btn-danger" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.65rem", padding: "0.1rem 0.3rem" }}
                            onClick={() => setPbDate(d)}
                          >
                            {d.slice(5)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="phone-auto-advance"
                      checked={pbAutoAdvance}
                      onChange={(e) => setPbAutoAdvance(e.target.checked)}
                    />
                    <label className="form-check-label text-muted small" htmlFor="phone-auto-advance">Auto-advance</label>
                  </div>
                </div>

                {clipList.error && <div className="text-warning small mt-2">{clipList.error}</div>}
              </div>
            )}
          </div>

          {/* Video */}
          <div className="rounded overflow-hidden mb-3" style={{ backgroundColor: "#000", minHeight: "200px" }}>
            {!pbSelectedFile ? (
              <div className="text-muted text-center p-4" style={centered}>
                <div>
                  <i className="fas fa-film fa-2x mb-2 d-block" aria-hidden="true"></i>
                  Select a clip from the timeline below
                </div>
              </div>
            ) : video.loading ? (
              <div className="text-muted text-center p-4" style={centered}>
                <div>
                  <i className="fas fa-circle-notch fa-spin fa-2x mb-2 d-block" aria-hidden="true"></i>
                  Loading video...
                </div>
              </div>
            ) : video.error ? (
              <div className="text-center p-4" style={{ ...centered, flexDirection: "column" }}>
                <div className="text-warning mb-2">{video.error}</div>
                <button type="button" className="btn btn-sm btn-outline-light" onClick={video.retry}>
                  <i className="fas fa-redo me-1" aria-hidden="true"></i> Retry
                </button>
              </div>
            ) : !video.url ? (
              <div className="text-muted text-center p-4" style={centered}>Preparing video...</div>
            ) : (
              <video
                key={video.url}
                src={video.url}
                controls
                playsInline
                data-testid="phone-clip-video"
                style={{ width: "100%", maxHeight: "50vh", backgroundColor: "#000", display: "block" }}
                onEnded={handleVideoEnded}
                onLoadedData={handleVideoLoaded}
              />
            )}
          </div>

          {pbSelectedFile && (
            <div className="text-muted small mb-2 text-center" data-testid="phone-now-playing">
              <i className="fas fa-play-circle me-1" aria-hidden="true"></i>
              {clipLabel(pbSelectedFile)} CT
            </div>
          )}

          {/* Timeline (newest first) */}
          <div className="rounded" style={{ backgroundColor: "#2d2d2d", border: "1px solid #444" }}>
            <div className="d-flex justify-content-between align-items-center p-2" style={{ borderBottom: "1px solid #444" }}>
              <span className="text-white fw-bold small">
                <i className="fas fa-clock me-1" aria-hidden="true"></i>Timeline
              </span>
              <span className="badge bg-secondary">{pbFiles.length}</span>
            </div>

            <div style={{ maxHeight: "40vh", overflowY: "auto" }} data-testid="phone-clip-list">
              {clipList.loading && (
                <div className="p-3 text-muted text-center small">
                  <i className="fas fa-circle-notch fa-spin me-1" aria-hidden="true"></i>Loading recordings...
                </div>
              )}
              {!clipList.loading && pbFiles.length === 0 && <div className="p-3 text-muted text-center small">No recordings found.</div>}

              {pbFiles.map((f) => {
                const thumbUrl = thumbs.thumbFor(f);
                const isPlaying = pbSelectedFile === f;
                return (
                  <button
                    key={f}
                    type="button"
                    ref={(el) => {
                      thumbs.observe(el);
                      if (isPlaying && el) activeClipRef.current = el;
                    }}
                    data-filename={f}
                    aria-current={isPlaying ? "true" : undefined}
                    className="d-flex align-items-center gap-2 px-2 py-2 w-100 border-0 text-start"
                    style={{
                      backgroundColor: isPlaying ? "#1a2332" : "transparent",
                      borderLeft: isPlaying ? "3px solid #dc3545" : "3px solid transparent",
                      borderBottom: "1px solid #3a3a3a",
                      minHeight: "48px",
                    }}
                    onClick={() => pickClip(f)}
                  >
                    <span
                      style={{
                        width: 96,
                        height: 54,
                        backgroundColor: "#111",
                        border: isPlaying ? "2px solid #dc3545" : "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 3,
                        overflow: "hidden",
                        flex: "0 0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <i className="fas fa-image text-muted" style={{ fontSize: "0.6rem" }} aria-hidden="true"></i>
                      )}
                      {isPlaying && (
                        <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.45)" }}>
                          <i className="fas fa-play" style={{ color: "#fff", fontSize: 14 }} aria-hidden="true"></i>
                        </span>
                      )}
                    </span>
                    <span className="flex-grow-1 d-flex justify-content-between align-items-center">
                      <span className={isPlaying ? "text-white fw-bold" : "text-white"} style={{ fontSize: "0.85rem" }}>
                        {isPlaying && <i className="fas fa-volume-up me-1" style={{ color: "#dc3545", fontSize: 10 }} aria-hidden="true"></i>}
                        {clipLabel(f)}
                        <small className="text-muted ms-1">CT</small>
                      </span>
                      <small className={isPlaying ? "text-white opacity-75" : "text-muted"}>2 min</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
